import { HierarcicalObjectReference, ObjectId } from "@novorender/data-js-api";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from "react";

import { Tree } from "components/objectTree/types";
import type { ObjectDataAccess } from "dataAccess";
import { ModelTreeNodeData } from "features/modelTree/types";
import { isLeafNode } from "utils/objectPath";
import { fetchDescendantsBatch } from "utils/preloadDescendants";

/**
 * Returns a callback that, when called with a node path after expansion,
 * preloads `descendants` for the newly visible child folder nodes in the
 * background. This warms the cache so that subsequent checkbox clicks on
 * those children resolve instantly without network calls.
 *
 * The returned callback only *buffers* the path: reading the expanded node's
 * children synchronously right after `setTree` would hit a stale tree (React
 * hasn't committed the new children yet). The buffered paths are drained once
 * the committed `tree` lands in the effect below, which fetches the children's
 * descendants in one bounded-concurrency batch ({@link fetchDescendantsBatch}).
 * Once a batch populates data the hook "touches" the tree (`setTree(t => ({...t}))`)
 * so memoised selection / visibility counts recompute — mirroring the refresh
 * {@link usePreloadDescendants} performs.
 *
 * Preloading is best-effort: failures are silently ignored and do not affect
 * the user experience.
 */
export function usePreloadOnExpand(
    dataAccess: ObjectDataAccess | undefined,
    tree: Tree<ModelTreeNodeData>,
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>,
): (expandedPath: string) => void {
    const queueRef = useRef<Set<string>>(new Set());
    const inflightRef = useRef<Set<ObjectId>>(new Set());
    const abortControllerRef = useRef<AbortController | null>(null);

    // Create a fresh AbortController per backend instance and cancel any in-flight
    // preloads on unmount or when it changes (e.g. scene switch), so stale requests
    // don't keep running or mutate descendants on outdated nodes.
    useEffect(() => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const queue = queueRef.current;
        const inflight = inflightRef.current;

        return () => {
            abortController.abort();
            abortControllerRef.current = null;
            queue.clear();
            inflight.clear();
        };
    }, [dataAccess]);

    // Drain buffered paths once the committed `tree` lands so we read the newly
    // loaded children rather than the pre-`setTree` snapshot.
    useEffect(() => {
        const signal = abortControllerRef.current?.signal;
        if (!dataAccess || !signal || signal.aborted || queueRef.current.size === 0) {
            return;
        }

        const paths = [...queueRef.current];
        queueRef.current.clear();

        const toFetch: HierarcicalObjectReference[] = [];
        for (const path of paths) {
            const node = tree[path];
            if (!node?.children) {
                continue;
            }
            for (const childPath of node.children) {
                const child = tree[childPath];
                if (
                    child &&
                    !child.isLeaf &&
                    !isLeafNode(child.data.object) &&
                    child.data.object.descendants === undefined &&
                    !inflightRef.current.has(child.data.object.id)
                ) {
                    toFetch.push(child.data.object);
                }
            }
        }

        if (toFetch.length === 0) {
            return;
        }

        const ids = toFetch.map((obj) => obj.id);
        for (const id of ids) {
            inflightRef.current.add(id);
        }

        fetchDescendantsBatch(dataAccess, toFetch, signal)
            .then((populated) => {
                if (populated && !signal.aborted) {
                    setTree((t) => ({ ...t }));
                }
            })
            .finally(() => {
                for (const id of ids) {
                    inflightRef.current.delete(id);
                }
            });
    }, [tree, dataAccess, setTree]);

    return useCallback((expandedPath: string) => {
        queueRef.current.add(expandedPath);
    }, []);
}
